import React, { useState, useEffect } from 'react';
import { PatientSummaryItem, TemplateItem } from '../context/StreamingContext';
import './CollapsibleSidebar.css';

interface SidebarProps {
  isOpen: boolean;
  patientData?: PatientSummaryItem[];
  templateData?: TemplateItem[];
  isLoading?: boolean;
}

const CollapsibleSidebar: React.FC<SidebarProps> = ({ isOpen, patientData, templateData, isLoading }) => {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    patientInfo: true,
    diagnostics: true,
  });

  // Debug logging
  // useEffect(() => {
  //   if (templateData && templateData.length > 0) {
  //     console.log('===== CollapsibleSidebar Template Data =====');
  //     console.log('Template data array length:', templateData.length);
  //     console.log('Full template data array:', templateData);
  //     templateData.forEach((item, index) => {
  //       console.log(`Template item ${index} keys:`, Object.keys(item));
  //     });
  //     console.log('==========================================');
  //   }
  // }, [templateData]);

  // Dynamically add sections for all template data keys
  useEffect(() => {
    if (templateData && templateData.length > 0) {
      setExpandedSections(prev => {
        const newSections: Record<string, boolean> = { ...prev };

        // Add sections for ALL keys in ALL template items
        templateData.forEach((item, index) => {
          Object.keys(item).forEach(key => {
            const sectionKey = `${index}-${key.toLowerCase().replace(/\s+/g, '')}`;
            if (!(sectionKey in newSections)) {
              newSections[sectionKey] = true;
            }
          });
        });

        return newSections;
      });
    }
  }, [templateData]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Helper function to render nested content
  const renderNestedContent = (data: any): React.ReactNode => {
    if (!data) return null;

    if (Array.isArray(data)) {
      return (
        <>
          {data.map((item, index) => {
            // Handle objects within arrays recursively
            if (typeof item === 'object' && item !== null) {
              return (
                <div key={index} className="assessment-item">
                  {renderNestedContent(item)}
                </div>
              );
            }
            // Handle primitive values
            return (
              <div className="assessment-item" key={index}>
                {String(item)}
              </div>
            );
          })}
        </>
      );
    }

    if (typeof data === 'object' && data !== null) {
      return (
        <>
          {Object.entries(data).map(([key, value]) => (
            <div key={key}>
              <div className="subsection-label">{key}</div>
              {renderNestedContent(value)}
            </div>
          ))}
        </>
      );
    }

    return <div className="assessment-item">{String(data)}</div>;
  };

  return (
    <div className={`sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-content">
        {/* Patient Information Section */}
        <div className="sidebar-section">
          <div className="section-header" onClick={() => toggleSection('patientInfo')}>
            <h3>Patient Information</h3>
            <div className="section-icons">
              <button className="icon-button arrow-button" aria-label={expandedSections.patientInfo ? "Collapse" : "Expand"}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={expandedSections.patientInfo ? 'rotated' : ''}
                >
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          {expandedSections.patientInfo && (
            <div className="section-content">
              {!patientData || patientData.length === 0 ? (
                <div className="skeleton-section">
                  <div className="skeleton-line skeleton-short"></div>
                  <div className="skeleton-line skeleton-medium"></div>
                  <div className="skeleton-line skeleton-short"></div>
                  <div className="skeleton-line skeleton-medium"></div>
                </div>
              ) : (
                patientData.map((item, index) => (
                  <div className="info-row" key={index}>
                    <span className="label">{item.field}</span>
                    <span className="value">{item.value || '-'}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Referral Information Section */}
        {/* <div className="sidebar-section">
          <div className="section-header" onClick={() => toggleSection('referralInfo')}>
            <h3>Referral Information</h3>
            <div className="section-icons">
              <button className="icon-button arrow-button" aria-label={expandedSections.referralInfo ? "Collapse" : "Expand"}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={expandedSections.referralInfo ? 'rotated' : ''}
                >
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          {expandedSections.referralInfo && (
            <div className="section-content">
              {!templateData || templateData.length === 0 ? (
                <div className="skeleton-section">
                  <div className="skeleton-line skeleton-medium"></div>
                  <div className="skeleton-line skeleton-short"></div>
                </div>
              ) : (() => {
                // Find specialist and template values across all array elements
                let specialist = '-';
                let template = '-';

                templateData.forEach(item => {
                  if (item.specialist) specialist = item.specialist;
                  if (item.template) template = item.template;
                });

                return (
                  <>
                    <div className="info-row">
                      <span className="label">Specialist</span>
                      <span className="value">{specialist}</span>
                    </div>
                    <div className="info-row">
                      <span className="label">Template</span>
                      <span className="value">{template}</span>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div> */}

        {/* Diagnostics Section */}
        <div className="sidebar-section">
          <div className="section-header" onClick={() => toggleSection('diagnostics')}>
            <h3>Diagnostics</h3>
            <div className="section-icons">
              <button className="icon-button arrow-button" aria-label={expandedSections.diagnostics ? "Collapse" : "Expand"}>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={expandedSections.diagnostics ? 'rotated' : ''}
                >
                  <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          </div>
          {expandedSections.diagnostics && (
            <div className="section-content sub-sections-container">
              {isLoading && (!templateData || (Array.isArray(templateData) && templateData.length === 0)) ? (
                <div className="skeleton-section" style={{ padding: '16px' }}>
                  <div className="skeleton-line skeleton-medium"></div>
                  <div className="skeleton-line skeleton-short"></div>
                  <div className="skeleton-line skeleton-medium"></div>
                </div>
              ) : (
                (() => {
                  const dataArray = Array.isArray(templateData) ? templateData : (templateData ? [templateData] : []);
                  if (dataArray.length === 0 && !isLoading) {
                    return <div className="no-data-msg" style={{ padding: '16px', fontSize: '13px', color: '#9ca3af' }}>No diagnostic data available.</div>;
                  }
                  
                  return dataArray.flatMap((item, itemIndex) => 
                    Object.entries(item).map(([keyName, value]) => {
                      if (keyName === 'specialist' || keyName === 'template') return null;

                      const sectionKey = `${itemIndex}-${keyName.toLowerCase().replace(/\s+/g, '')}`;
                      const isExpanded = expandedSections[sectionKey];

                      let summaryValue = '-';
                      if (typeof value === 'string' || typeof value === 'number') {
                        summaryValue = String(value);
                      } else if (Array.isArray(value) && value.length > 0) {
                        if (typeof value[0] === 'string') {
                          summaryValue = value[0];
                        } else if (typeof value[0] === 'object' && value[0] !== null) {
                          summaryValue = value[0].value || value[0].name || value[0].result || 'View Details';
                        }
                      } else if (typeof value === 'object' && value !== null) {
                        summaryValue = 'View Details';
                      }

                      return (
                        <div className="sub-section" key={sectionKey}>
                          <div className="sub-section-header" onClick={() => toggleSection(sectionKey)}>
                            <span className="label">{keyName}</span>
                            <div className="header-right">
                              {/* <span className="value">{summaryValue}</span> */}
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                className={isExpanded ? 'rotated' : ''}
                              >
                                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            </div>
                          </div>
                          {isExpanded && (
                            <div className="sub-section-content">
                              {renderNestedContent(value)}
                            </div>
                          )}
                        </div>
                      );
                    })
                  ).filter(Boolean);
                })()
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSidebar;
